/**
 * Team names and club channel names, shared by the app and the relay
 * (apps/relay/src/sports imports this file), so both match a club the same way:
 * "Newcastle United" = "NEWCASTLE UTD", "FC Barcelona" = "BARCELONA", "Man City" = "Manchester City".
 * No imports: the relay runs it with plain Node.
 */

/** Lowercase, no accents, no punctuation. */
export function fold(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9؀-ۿ]+/g, ' ')
    .trim();
}

/** Words that tell nothing about which team it is. */
const FILLER = new Set(['fc', 'cf', 'sc', 'ac', 'afc', 'as', 'ss', 'sk', 'fk', 'cd', 'ud', 'sd', 'rc', 'rcd', 'club', 'de', 'del', 'la', 'the', 'calcio', 'a', 's', 'sv', 'vfb', 'vfl', 'tsg', 'bk', 'if', 'ks', 'cp', 'sl', 'sad', 'f', 'cluj']);
/** Other names of the same team (folded). */
const ALIASES: Record<string, string> = {
  psg: 'paris saint germain', 'paris sg': 'paris saint germain', 'paris': 'paris saint germain',
  'man city': 'manchester city', 'man utd': 'manchester united', 'man united': 'manchester united', 'nottm forest': 'nottingham forest', 'notts forest': 'nottingham forest',
  inter: 'internazionale', 'inter milan': 'internazionale', 'internazionale milano': 'internazionale',
  barca: 'barcelona', spurs: 'tottenham hotspur', tottenham: 'tottenham hotspur', wolves: 'wolverhampton wanderers',
  'atl madrid': 'atletico madrid', atletico: 'atletico madrid', 'atletico de madrid': 'atletico madrid',
  bayern: 'bayern munchen', 'bayern munich': 'bayern munchen', 'fc bayern': 'bayern munchen',
  bvb: 'borussia dortmund', dortmund: 'borussia dortmund', leverkusen: 'bayer leverkusen', gladbach: 'borussia monchengladbach',
  juve: 'juventus', napoli: 'napoli', roma: 'roma', milan: 'milan', 'ac milan': 'milan',
  wac: 'wydad', 'wydad casablanca': 'wydad', 'wydad ac': 'wydad', rca: 'raja', 'raja casablanca': 'raja', 'raja ca': 'raja', far: 'far rabat', 'as far': 'far rabat',
  'al hilal saudi': 'al hilal', 'al nassr riyadh': 'al nassr', 'al ahly cairo': 'al ahly',
  // National teams in Spanish, French, Italian and Portuguese.
  francia: 'france', franca: 'france', gana: 'ghana', japon: 'japan', giappone: 'japan', brasil: 'brazil', bresil: 'brazil', brasile: 'brazil',
  alemania: 'germany', allemagne: 'germany', germania: 'germany', alemanha: 'germany', espana: 'spain', espagne: 'spain', spagna: 'spain', espanha: 'spain',
  inglaterra: 'england', angleterre: 'england', inghilterra: 'england', marruecos: 'morocco', maroc: 'morocco', marocco: 'morocco', marrocos: 'morocco',
  noruega: 'norway', norvege: 'norway', norvegia: 'norway', italie: 'italy', italia: 'italy', holanda: 'netherlands', 'paises bajos': 'netherlands', 'pays bas': 'netherlands', olanda: 'netherlands',
  argentine: 'argentina', belgique: 'belgium', belgica: 'belgium', belgio: 'belgium', croacia: 'croatia', croatie: 'croatia', croazia: 'croatia',
  'estados unidos': 'usa', 'etats unis': 'usa', 'united states': 'usa', 'stati uniti': 'usa', mexique: 'mexico', messico: 'mexico',
  'corea del norte': 'north korea', 'coree du nord': 'north korea', 'corea del sur': 'south korea', 'coree du sud': 'south korea',
  egipto: 'egypt', egypte: 'egypt', egitto: 'egypt', argelia: 'algeria', algerie: 'algeria', tunez: 'tunisia', tunisie: 'tunisia',
  senegal: 'senegal', 'arabia saudi': 'saudi arabia', 'arabie saoudite': 'saudi arabia', suiza: 'switzerland', suisse: 'switzerland', svizzera: 'switzerland',
  suecia: 'sweden', suede: 'sweden', svezia: 'sweden', dinamarca: 'denmark', danemark: 'denmark', polonia: 'poland', pologne: 'poland', turquia: 'turkey', turquie: 'turkey', turchia: 'turkey',
  'reino unido': 'united kingdom', escocia: 'scotland', ecosse: 'scotland', gales: 'wales', 'pays de galles': 'wales', irlanda: 'ireland', irlande: 'ireland',
};

const wordsCache = new Map<string, string[]>();

/** National teams the aliases translate to. */
const COUNTRY_NAMES = new Set(['france', 'japan', 'brazil', 'germany', 'spain', 'england', 'morocco', 'norway', 'italy', 'netherlands', 'argentina', 'belgium', 'croatia', 'usa', 'mexico', 'egypt', 'algeria', 'tunisia', 'switzerland', 'sweden', 'denmark', 'poland', 'turkey', 'scotland', 'wales', 'ireland']);

/** The words that identify a team ("FC Internazionale Milano" → internazionale). Women's and youth teams keep their marker. */
export function teamWords(name: string): string[] {
  let words = wordsCache.get(name);
  if (!words) {
    words = computeTeamWords(name);
    if (wordsCache.size > 50_000) wordsCache.clear();
    wordsCache.set(name, words);
  }
  return words;
}

function computeTeamWords(name: string): string[] {
  let folded = fold(name.replace(/\(F\)|\bW\b|\bWomen\b|\bFemenino\b|\bFéminine\b|\bFemminile\b|\bFrauen\b/gi, ' women '));
  const alias = aliasOf(folded) ?? aliasOf(folded.replace(/\b(fc|cf|sc|ac|afc|as|club)\b/g, '').replace(/\s+/g, ' ').trim());
  if (alias) folded = alias;
  // "UTD" is "United" everywhere ("EPL NEWCASTLE UTD [UK]" for Newcastle United, Abel 2026-10-29).
  const words = folded.split(' ').filter((word) => word && !FILLER.has(word)).map((word) => (word === 'utd' ? 'united' : word));
  // Word by word only for national teams ("Japón (F)" → japan women); club nicknames ("Inter") only as a whole name.
  return words.map((word) => {
    const alias = aliasOf(word);
    return alias && COUNTRY_NAMES.has(alias) ? alias : word;
  });
}

/** An alias by own key only ("constructor" isn't one). */
function aliasOf(name: string): string | undefined {
  return Object.hasOwn(ALIASES, name) ? ALIASES[name] : undefined;
}

export const MARKERS = /^(women|u\d{2}|u-\d{2}|ii|b|reserves|youth)$/;

/** Whether two names mean the same team: one's words within the other's, with the same women/youth markers. */
export function sameTeam(a: string, b: string): boolean {
  const x = teamWords(a);
  const y = teamWords(b);
  if (x.length === 0 || y.length === 0) return false;
  const markersX = x.filter((word) => MARKERS.test(word)).sort().join();
  const markersY = y.filter((word) => MARKERS.test(word)).sort().join();
  if (markersX !== markersY) return false;
  const [small, big] = x.length <= y.length ? [x, y] : [y, x];
  return small.every((word) => big.includes(word)) && small.some((word) => word.length >= 3 && !MARKERS.test(word));
}

/* Club channels (#115) */

export type ClubCountry = 'england' | 'spain';

/** A 24/7 club channel's club, as read from its name. */
export interface ClubName {
  /** The club as the channel names it ("SUNDERLAND"). */
  club: string;
  country: ClubCountry;
  /** The prefix it was found under ("UK| EPL"), for the relay log. */
  prefix: string;
  /** Commentary language when the name or category doesn't say. */
  language: string;
}

/**
 * The providers' club channel prefixes. `UK| EPL` and `LA LIGA|` are seen in the
 * provider list (Abel, 2026-10-28); the others are guesses for the Championship
 * and LaLiga under another prefix, harmless when absent.
 */
const PREFIXES: { pattern: RegExp; prefix: string; country: ClubCountry; language: string }[] = [
  { pattern: /^UK\s*[|:]\s*EPL\s+(.+)$/i, prefix: 'UK| EPL', country: 'england', language: 'English' },
  { pattern: /^UK\s*[|:]\s*(?:EFL|CHAMPIONSHIP|SKY ?BET)\s+(.+)$/i, prefix: 'UK| EFL', country: 'england', language: 'English' },
  { pattern: /^LA ?LIGA\s*[|:]\s*(.+)$/i, prefix: 'LA LIGA|', country: 'spain', language: 'Spanish' },
  { pattern: /^(?:ES|SPAIN)\s*[|:]\s*LA ?LIGA\s+(.+)$/i, prefix: 'ES| LALIGA', country: 'spain', language: 'Spanish' },
  // The country as a tag at the end ("EPL MANCHESTER CITY [UK]", Abel 2026-10-28 evening).
  { pattern: /^EPL\s+(.+?)\s*\[(?:UK|GB|EN)\]/i, prefix: 'EPL [UK]', country: 'england', language: 'English' },
  { pattern: /^(?:EFL|CHAMPIONSHIP|SKY ?BET)\s+(.+?)\s*\[(?:UK|GB|EN)\]/i, prefix: 'EFL [UK]', country: 'england', language: 'English' },
  { pattern: /^LA ?LIGA\s+(.+?)\s*\[(?:ES|SPAIN)\]/i, prefix: 'LALIGA [ES]', country: 'spain', language: 'Spanish' },
];

/** Quality and feed words after the club ("ARSENAL FHD", "BARCELONA ᴴᴰ", "SUNDERLAND (BACKUP)"). */
const TRAILING = /(?:\s+(?:UHD|FHD|HD|SD|4K|8K|HEVC|H265|RAW|ᴴᴰ|ᶠᴴᴰ|ᵁᴴᴰ|ᴿᴬᵂ|\d{3,4}P|\d+FPS)|\s*\([^)]*\)|\s*\[[^\]]*\])+\s*$/i;

/** A channel's club, when it is a club channel under a known prefix. */
export function clubChannelOf(name: string): ClubName | null {
  const trimmed = name.trim();
  // An event channel under the same prefix ("UK| EPL Arsenal vs Chelsea") is not a club channel.
  if (/\s(?:vs?\.?|x)\s|\s@\s|\d{1,2}:\d{2}/i.test(trimmed)) return null;
  for (const entry of PREFIXES) {
    const found = entry.pattern.exec(trimmed);
    if (!found) continue;
    const club = found[1]!.replace(TRAILING, '').replace(/\s*\|.*$/, '').trim();
    // A club, not a programme or number ("UK| EPL 1", "UK| EPL HIGHLIGHTS").
    if (!/[a-z]{3}/i.test(club) || /\b(highlights|replay|news|tv|channel|live|pass|match|game|studio|show)\b/i.test(club)) return null;
    if (teamWords(club).length === 0) return null;
    return { club, country: entry.country, prefix: entry.prefix, language: entry.language };
  }
  return null;
}
